import React from 'react';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import FooterLayout from 'layouts/FooterLayout';
import Logo from 'logos/Logo';
import { kosmosLanding } from 'kosmos/images';
import { useTranslation } from 'react-i18next';
import LocaleButton from 'components/LocaleButton';

const PREFIX = 'LandingPage';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  toolbarButtons: `${PREFIX}-toolbarButtons`,
  red: `${PREFIX}-red`,
  green: `${PREFIX}-green`,
  appBar: `${PREFIX}-appBar`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  title1: `${PREFIX}-title1`,
  imageContainer: `${PREFIX}-imageContainer`,
  title2: `${PREFIX}-title2`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    '& > *': {
      flex: 1,
    },
  },

  [`& .${classes.toolbarButtons}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
      marginLeft: theme.spacing(2),
    },
  },

  [`& .${classes.red}`]: {
    backgroundColor: red,
  },

  [`& .${classes.green}`]: {
    backgroundColor: green,
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
  },

  [`& .${classes.appBarSpacer}`]: {
    minHeight: 70,
  },

  [`& .${classes.contentContainer}`]: {
    background: 'radial-gradient(circle, rgba(68,140,203,1) 0%, rgba(38,50,105,1) 100%)',
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    textAlign: 'center',
    margin: 'auto',
    maxWidth: 1600,
    paddingBottom: 50,
  },

  [`& .${classes.title1}`]: {
    marginLeft: 40,
    fontWeight: 'bolder',
    margin: 0,
    lineHeight: 1.1,
    fontSize: 45,
    color: 'white',
    [theme.breakpoints.down('xs')]: {
      fontSize: 25,
    },
  },

  [`& .${classes.imageContainer}`]: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 600,
      maxWidth: 800,
      margin: '0 auto',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },

  [`& .${classes.title2}`]: {
    fontSize: 18,
    lineHeight: 1,
    color: 'white',
    textTransform: 'uppercase',
  },
}));

const red = '#FF75A8';
const green = '#26C9A8';

interface Login {
  (): void;
}

interface Props {
  login?: Login;
  children?: React.ReactNode;
}

export default function LandingPage(props: Props): JSX.Element {
  const { login, children } = props;
  const { t } = useTranslation('default');

  return (
    <Root className={classes.root}>
      <CssBaseline />
      <AppBar position="absolute" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div>
            <Logo color="white" height={40} />
          </div>
          <div className={classes.toolbarButtons}>
            <LocaleButton />
            {/* <ColorButton variant="contained" color="primary" className={classes.red} > Subscribe </ColorButton> */}
            <Button variant="contained" color="secondary" onClick={login}>
              {t('login')}
            </Button>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.contentContainer}>
        <div className={classes.appBarSpacer} />
        <div>{children}</div>
        <div className={classes.content}>
          <div className={classes.title1}>{t('esg-ratings')}</div>
          <div className={classes.imageContainer}>
            <img width="100%" src={kosmosLanding} alt="ESG tracker" />
          </div>
          <div className={classes.title2}>
            <div>
              {t('the-only-fully-automated-standardised-tool-for-measuring-esg-sustainability')}
            </div>
          </div>
        </div>
        <FooterLayout />
      </main>
    </Root>
  );
}
